"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import { hmsText } from "@/components/tools/physics-mechanics/speed-trip";
import { Working } from "@/components/tools/physics-mechanics/working";
import type { SPEED_DISTANCE_TIME_CALCULATOR_STRINGS } from "@/components/tools/speed-distance-time-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  clock,
  paceSeconds,
  type SdtKnown,
  type SdtResult,
  type SdtUnknown,
  solveSdt,
} from "@/lib/tool-kit/physics-mechanics/speed-distance-time";
import {
  convert,
  fromBase,
  isBaseUnit,
  LENGTH,
  SPEED,
  TIME,
  toBase,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

type Strings = typeof SPEED_DISTANCE_TIME_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
export type TimeEntry = "single" | "hms";
export type Slot = "distance" | "speed" | "time";

export const SLOTS: readonly Slot[] = ["distance", "speed", "time"];
export const FAMILIES = { distance: LENGTH, speed: SPEED, time: TIME } as const;
const PACE_KM = 1000;
const PACE_MILE = 1609.344;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

export interface Entry {
  value: string;
  unit: string;
}

export type Entries = Record<Slot, Entry>;

function readTime(
  entries: Entries,
  entry: TimeEntry,
  hms: { h: string; m: string; s: string }
): number | null | "empty" {
  if (entry === "single") {
    if (entries.time.value.trim() === "") {
      return "empty";
    }
    const value = parseQuantity(entries.time.value);
    return value === null ? null : toBase(value, TIME, entries.time.unit);
  }
  const parts = [hms.h, hms.m, hms.s];
  if (parts.every((part) => part.trim() === "")) {
    return "empty";
  }
  const [h, m, s] = parts.map((part) =>
    part.trim() === "" ? 0 : parseQuantity(part)
  );
  if (h === null || m === null || s === null) {
    return null;
  }
  return h * SECONDS_PER_HOUR + m * SECONDS_PER_MINUTE + s;
}

interface Reading {
  known: SdtKnown;
  missing: boolean;
  unreadable: Slot | null;
}

export function readSingle(
  unknown: SdtUnknown,
  entries: Entries,
  entry: TimeEntry,
  hms: { h: string; m: string; s: string }
): Reading {
  const known: SdtKnown = {};
  let missing = false;
  for (const slot of SLOTS) {
    if (slot === unknown) {
      continue;
    }
    if (slot === "time") {
      const time = readTime(entries, entry, hms);
      if (time === null) {
        return { known, missing, unreadable: "time" };
      }
      if (time === "empty") {
        missing = true;
      } else {
        known.time = time;
      }
      continue;
    }
    const raw = entries[slot].value.trim();
    if (raw === "") {
      missing = true;
      continue;
    }
    const value = parseQuantity(raw);
    if (value === null) {
      return { known, missing, unreadable: slot };
    }
    known[slot] = toBase(value, FAMILIES[slot], entries[slot].unit);
  }
  return { known, missing, unreadable: null };
}

export function showValue(
  slot: Slot,
  si: number,
  unit: string,
  fmt: Fmt,
  sig: number
): string {
  return formatSig(fmt, fromBase(si, FAMILIES[slot], unit), sig);
}

function siList(
  unknown: SdtUnknown,
  entries: Entries,
  known: SdtKnown,
  strings: Strings,
  fmt: Fmt,
  timeEntry: TimeEntry
): string | null {
  const plain = (value: number) => formatPlain(fmt, value);
  const letters = {
    distance: strings.triangleD,
    speed: strings.triangleS,
    time: strings.triangleT,
  };
  const list: string[] = [];
  for (const slot of SLOTS) {
    const si = known[slot];
    if (slot === unknown || si === undefined) {
      continue;
    }
    const typedInBase = isBaseUnit(FAMILIES[slot], entries[slot].unit);
    const fromParts = slot === "time" && timeEntry === "hms";
    if (!typedInBase || fromParts) {
      list.push(`${letters[slot]} = ${plain(si)} ${FAMILIES[slot].base}`);
    }
  }
  return list.length > 0 ? list.join("; ") : null;
}

function workingSteps(
  unknown: SdtUnknown,
  result: Extract<SdtResult, { ok: true }>,
  entries: Entries,
  known: SdtKnown,
  strings: Strings,
  fmt: Fmt,
  sig: number,
  timeEntry: TimeEntry
): string[] {
  const plain = (value: number) => formatPlain(fmt, value);
  const steps: string[] = [];
  const converted = siList(unknown, entries, known, strings, fmt, timeEntry);
  if (converted !== null) {
    steps.push(t(strings.stepSi, { list: converted }));
  }
  const d = `${plain(result.distance)} m`;
  const v = `${plain(result.speed)} m/s`;
  const time = `${plain(result.time)} s`;
  if (unknown === "speed") {
    steps.push(strings.stepFormulaSpeed);
    steps.push(
      t(strings.workSpeed, {
        distance: d,
        time,
        result: `${formatSig(fmt, result.speed, sig)} m/s`,
      })
    );
  } else if (unknown === "distance") {
    steps.push(strings.stepFormulaDistance);
    steps.push(
      t(strings.workDistance, {
        speed: v,
        time,
        result: `${formatSig(fmt, result.distance, sig)} m`,
      })
    );
  } else {
    steps.push(strings.stepFormulaTime);
    steps.push(
      t(strings.workTime, {
        distance: d,
        speed: v,
        result: `${formatSig(fmt, result.time, sig)} s`,
      })
    );
  }
  const si = {
    speed: `${formatSig(fmt, result.speed, sig)} m/s`,
    distance: `${formatSig(fmt, result.distance, sig)} m`,
    time: `${formatSig(fmt, result.time, sig)} s`,
  }[unknown];
  if (!isBaseUnit(FAMILIES[unknown], entries[unknown].unit)) {
    const shown = `${showValue(unknown, result[unknown], entries[unknown].unit, fmt, sig)} ${unitSymbol(FAMILIES[unknown], entries[unknown].unit)}`;
    steps.push(t(strings.stepConvert, { si, shown }));
  }
  return steps;
}

function EquivalentSpeeds({
  speed,
  strings,
  fmt,
  sig,
}: {
  speed: number;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const rows: string[][] = SPEED.units.map((unit) => [
    unit.symbol,
    formatSig(fmt, convert(speed, SPEED, "mps", unit.id), sig),
  ]);
  if (speed > 0) {
    rows.push([strings.pacePerKm, clock(paceSeconds(speed, PACE_KM))]);
    rows.push([strings.pacePerMile, clock(paceSeconds(speed, PACE_MILE))]);
  }
  return (
    <div className="space-y-2">
      <p className={LABEL}>{strings.equivalentHeading}</p>
      <DataTable head={[strings.columnUnit, strings.columnValue]} rows={rows} />
    </div>
  );
}

export interface FormState {
  unknown: SdtUnknown;
  entries: Entries;
  timeEntry: TimeEntry;
  hms: { h: string; m: string; s: string };
}

export function SingleResults({
  form,
  strings,
  fmt,
  sig,
}: {
  form: FormState;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const { unknown, entries } = form;
  const reading = readSingle(unknown, entries, form.timeEntry, form.hms);
  if (reading.unreadable !== null) {
    return (
      <ErrorNotice>
        {t(strings.unreadable, { name: strings[reading.unreadable] })}
      </ErrorNotice>
    );
  }
  if (reading.missing) {
    return <Notice>{strings.enterAll}</Notice>;
  }
  const result = solveSdt(unknown, reading.known);
  if (!result.ok) {
    return <ErrorNotice>{strings.errors[result.error]}</ErrorNotice>;
  }
  const answer = `${showValue(unknown, result[unknown], entries[unknown].unit, fmt, sig)} ${unitSymbol(FAMILIES[unknown], entries[unknown].unit)}`;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings[unknown]} value={answer} />
        {unknown === "time" ? null : (
          <StatCard
            label={strings.time}
            value={hmsText(result.time, strings, fmt)}
          />
        )}
        {unknown === "time" ? (
          <StatCard
            label={strings.timeAs}
            value={hmsText(result.time, strings, fmt)}
          />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={`${strings[unknown]}: ${answer}`}
      />
      <Working
        heading={strings.workingHeading}
        steps={workingSteps(
          unknown,
          result,
          entries,
          reading.known,
          strings,
          fmt,
          sig,
          form.timeEntry
        )}
      />
      <EquivalentSpeeds
        fmt={fmt}
        sig={sig}
        speed={result.speed}
        strings={strings}
      />
      <p className={HINT}>{strings.assumptions}</p>
    </>
  );
}
